import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import * as api from "../../lib/api";

const MOTIF_OPTIONS = [
    { id: "client_trip", label: "Déplacement client", icon: "bg-blue-400" },
    { id: "training", label: "Formation", icon: "bg-green-400" },
    { id: "internal_event", label: "Événement interne", icon: "bg-purple-400" },
    { id: "other", label: "Autre", icon: "bg-gray-400" },
];

function OptionWithDot({ icon, label }) {
    return (
        <span className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${icon}`} />
            {label}
        </span>
    );
}

export function EquipmentLoanForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("low");
    const [equipment, setEquipment] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState("");
    const [dateStart, setDateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const [destination, setDestination] = useState("");
    const [motif, setMotif] = useState("client_trip");
    const [goodConditionConfirmed, setGoodConditionConfirmed] = useState(false);

    useEffect(() => {
        api.getEquipmentCatalog().then(setEquipment).catch(console.error);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const eq = equipment.find((eq) => eq.id === selectedEquipment);
        onSubmit({
            title: baseForm.title || `Prêt matériel — ${eq?.name || selectedEquipment}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                equipment_id: selectedEquipment,
                equipment_name: eq?.name || selectedEquipment,
                date_start: dateStart,
                date_end: dateEnd,
                destination: destination || null,
                motif: motif,
                good_condition_confirmed: goodConditionConfirmed,
                // IT agent fills these at closure
                returned_date: null,
                return_condition: null,
            },
        });
    };

    const currentMotif = MOTIF_OPTIONS.find((m) => m.id === motif);

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Prêt projecteur pour formation du 15/04"
                descriptionPlaceholder="Précisez le contexte : lieu, durée, usage prévu..."
            >
                {/* Equipment */}
                <div className="form-group">
                    <Label className="form-label">Équipement demandé</Label>
                    <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                        <SelectTrigger data-testid="equipment-select">
                            <SelectValue placeholder="Sélectionner un équipement" />
                        </SelectTrigger>
                        <SelectContent>
                            {equipment.map((eq) => (
                                <SelectItem key={eq.id} value={eq.id}>{eq.name} — {eq.type}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Date start */}
                    <div className="form-group">
                        <Label className="form-label">Date de départ</Label>
                        <Input
                            type="date"
                            value={dateStart}
                            onChange={(e) => setDateStart(e.target.value)}
                            data-testid="date-start"
                        />
                    </div>

                    {/* Date end */}
                    <div className="form-group">
                        <Label className="form-label">Retour prévu</Label>
                        <Input
                            type="date"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                            data-testid="date-end"
                        />
                    </div>
                </div>

                {/* Destination */}
                <div className="form-group">
                    <Label className="form-label">Lieu de destination</Label>
                    <Input
                        value={destination}
                        onChange={(e) => setDestination(e.target.value)}
                        placeholder="Ex: Bureau Lausanne, client XYZ, salle de formation..."
                        data-testid="destination"
                    />
                </div>

                {/* Motif */}
                <div className="form-group">
                    <Label className="form-label">Motif</Label>
                    <Select value={motif} onValueChange={setMotif}>
                        <SelectTrigger data-testid="motif">
                            <SelectValue>
                                {currentMotif && <OptionWithDot icon={currentMotif.icon} label={currentMotif.label} />}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {MOTIF_OPTIONS.map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                    <OptionWithDot icon={m.icon} label={m.label} />
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Good condition confirmed */}
                <div className="form-group">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="condition"
                            checked={goodConditionConfirmed}
                            onCheckedChange={setGoodConditionConfirmed}
                            data-testid="good-condition"
                        />
                        <Label htmlFor="condition" className="text-sm font-medium cursor-pointer">
                            Je confirme que le matériel est en bon état au moment du départ
                        </Label>
                    </div>
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button
                    type="submit"
                    disabled={!baseForm.title || submitting}
                    data-testid="submit-ticket"
                >
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
